/**
 * Single source of truth for backend feature flags.
 *
 * Fetches `/api/settings/flags` on auth-ready and re-fetches on `focus` +
 * every 60s, so toggling the flag in the System Flags page propagates to
 * the sidebar + protected routes within at most a minute without a reload.
 *
 * Components consume via the `useFeatureFlags()` hook:
 *
 *   const { dangerToolsEnabled, refresh } = useFeatureFlags();
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const DEFAULTS = { danger_tools_enabled: false, pf_enabled: false, launch_email_blast_enabled: true };

const FeatureFlagsContext = createContext({
  flags: DEFAULTS,
  dangerToolsEnabled: false,
  launchEmailBlastEnabled: true,
  loading: true,
  refresh: () => {},
});

export const FeatureFlagsProvider = ({ children }) => {
  const { user } = useAuth();
  const [flags, setFlags] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/settings/flags');
      if (r.data?.success) setFlags({ ...DEFAULTS, ...(r.data.data || {}) });
    } catch {
      // Conservative default — if the call fails for any reason, keep the
      // surfaces hidden. Better to under-show than over-show destructive
      // tools when we're unsure.
      setFlags(DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, []);

  // Bootstrap on login, refresh on tab focus, and every 60s as a safety net.
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    refresh();
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    const id = setInterval(refresh, 60000);
    return () => { window.removeEventListener('focus', onFocus); clearInterval(id); };
  }, [user, refresh]);

  return (
    <FeatureFlagsContext.Provider value={{
      flags,
      dangerToolsEnabled: !!flags.danger_tools_enabled,
      launchEmailBlastEnabled: flags.launch_email_blast_enabled !== false,
      loading,
      refresh,
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
